import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { DAY, toTs, fmtDate } from "../lib/measurements";

function goalSteps(subgoals, goal, goalDate, start) {
  const pts = [...subgoals].sort((a, b) => a.datum.localeCompare(b.datum)).map((g) => ({ v: Number(g.waarde), t: toTs(g.datum) }));
  if (goal !== null && goalDate) pts.push({ v: goal, t: toTs(goalDate) });
  if (!pts.length) return [];
  const out = [{ t: start, goal: pts[0].v }];
  pts.forEach((p, i) => out.push({ t: p.t, goal: pts[i + 1]?.v ?? p.v }));
  return out;
}

function Tip({ active, payload, sfx }) {
  const p = payload?.find((x) => x.payload?.nr)?.payload;
  if (!active || !p) return null;
  return (
    <div className="bg-zinc-900 text-white px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-zinc-400">#{p.nr} · {fmtDate(p.t)}</div>
      <div className="text-base font-grotesk font-semibold">{p.waarde}{sfx}</div>
      <div>{p.soort === "nulmeting" ? "Nulmeting" : `Interventie ${p.interventie_nummer}`}</div>
    </div>
  );
}

const Dot = ({ cx, cy, payload }) => (payload.soort === "nulmeting"
  ? <rect x={cx - 5} y={cy - 5} width={10} height={10} fill="#fff" stroke="#18181b" strokeWidth={2} />
  : <circle cx={cx} cy={cy} r={5.5} fill="#18181b" stroke="#fff" strokeWidth={2} />);

export const MeasurementChart = ({ pvp, cfg, points, interventions }) => {
  const start0 = new Date(pvp.created_at).setHours(0, 0, 0, 0);
  const start = Math.min(start0, ...points.map((p) => p.t - DAY), ...cfg.subgoals.map((g) => toTs(g.datum) - DAY));
  const last = Math.max(start + 30 * DAY, ...points.map((p) => p.t), cfg.goalDate ? toTs(cfg.goalDate) : 0);
  const steps = goalSteps(cfg.subgoals, cfg.goal, cfg.goalDate, start);
  const vals = [...points.map((p) => p.waarde), ...steps.map((s) => s.goal), cfg.base ?? 0];
  const yMin = cfg.lo ?? Math.min(0, ...vals);
  const yMax = cfg.hi ?? Math.ceil(Math.max(...vals, 1) * 1.15);
  const base = points.filter((p) => p.soort === "nulmeting");
  const meas = points.filter((p) => p.soort === "meting");
  const phaseStarts = interventions.map((i) => ({ ...i, t: meas.find((m) => m.interventie_nummer === i.nummer)?.t })).filter((i) => i.t);
  const ticks = [];
  for (let d = new Date(start); d.getTime() <= last + 2 * DAY; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) ticks.push(d.getTime());
  const phases = interventions.map((i) => ({ nr: i.nummer, data: meas.filter((m) => m.interventie_nummer === i.nummer) })).filter((p) => p.data.length);

  return (
    <div className="h-[420px] w-full" data-testid="measurement-chart">
      <ResponsiveContainer>
        <ComposedChart margin={{ top: 28, right: 24, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#e4e4e7" strokeDasharray="2 4" vertical={false} />
          <XAxis type="number" dataKey="t" domain={[start, last + 2 * DAY]} scale="time" ticks={ticks} interval="preserveStartEnd" tickFormatter={fmtDate}
            tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace", fill: "#71717a" }} minTickGap={24} stroke="#18181b" allowDuplicatedCategory={false} />
          <YAxis type="number" domain={[yMin, yMax]} tickFormatter={(v) => `${v}${cfg.sfx}`} width={56}
            tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace", fill: "#71717a" }} stroke="#18181b" />
          <Tooltip content={<Tip sfx={cfg.sfx} />} cursor={{ stroke: "#a1a1aa", strokeDasharray: "3 3" }} />
          {steps.length > 0 && <Line data={steps} dataKey="goal" type="stepAfter" stroke="#a1a1aa" strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} name="Subdoelen" />}
          {cfg.goal !== null && <ReferenceLine y={cfg.goal} stroke="#18181b" strokeWidth={1} label={{ value: `Hoofddoel ${cfg.goal}${cfg.sfx}`, position: "insideTopRight", fontSize: 11, fill: "#18181b" }} />}
          {cfg.base !== null && <ReferenceLine y={cfg.base} stroke="#a1a1aa" label={{ value: `Nulmeting ${cfg.base}${cfg.sfx}`, position: "insideBottomRight", fontSize: 11, fill: "#71717a" }} />}
          {phaseStarts.map((i) => (
            <ReferenceLine key={i.nummer} x={i.t - DAY / 2} stroke="#18181b" strokeDasharray="2 2"
              label={{ value: `I${i.nummer}`, position: "top", fontSize: 11, fontWeight: 700, fill: "#18181b" }} />
          ))}
          {base.length > 0 && <Line data={base} dataKey="waarde" stroke="#71717a" strokeWidth={1.5} dot={<Dot />} isAnimationActive={false} name="Nulmeting" />}
          {phases.map((p) => <Line key={p.nr} data={p.data} dataKey="waarde" stroke="#18181b" strokeWidth={2.5} dot={<Dot />} activeDot={{ r: 7 }} name={`Interventie ${p.nr}`} />)}
          {points.length === 0 && null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
